/**
 * ITU Floorplan Block – view.js  (v6 + fix double-init)
 *
 * Layout per panel:
 *   ┌──────────────────────────────────┐
 *   │  fp-detail  (big card, above)    │  ← always visible, updates on click
 *   └──────────────────────────────────┘
 *   ┌────┐ ┌────┐ ┌────┐ ┌────┐
 *   │    │ │    │ │    │ │    │  ← fp-room-grid
 *   └────┘ └────┘ └────┘ └────┘
 *    active card is hidden (is-active → display:none)
 *    so the selected room only appears in the detail above
 *
 * v6 features:
 *   – Search bar (.fp-search__input) filters rooms across all buildings
 *   – Matches highlighted, non-matches hidden, no-result message per panel
 *
 * v6.1 fix:
 *   – Guard against double-initialization (data-fp-initialized flag)
 *   – Detect & reuse existing .fp-tabs-nav wrapper (don't recreate)
 *   – Clean up nested wrappers if page already in broken state
 *
 * Security: zero innerHTML with raw user data.
 */
( () => {

	const svgNS = 'http://www.w3.org/2000/svg';

	// ── Tiny DOM helpers ──────────────────────────────────────────────────

	const el = ( tag, attrs = {}, ...children ) => {
		const node = document.createElement( tag );
		Object.entries( attrs ).forEach( ( [ k, v ] ) => {
			if ( k === 'class' ) node.className = v;
			else node.setAttribute( k, v );
		} );
		children.forEach( ( child ) => {
			if ( child == null ) return;
			node.appendChild(
				typeof child === 'string'
					? document.createTextNode( child )
					: child
			);
		} );
		return node;
	};

	const makePlaceholderSVG = ( size = 48 ) => {
		const svg = document.createElementNS( svgNS, 'svg' );
		svg.setAttribute( 'width',   size );
		svg.setAttribute( 'height',  size );
		svg.setAttribute( 'viewBox', '0 0 48 48' );
		svg.setAttribute( 'fill',    'none' );
		svg.setAttribute( 'aria-hidden', 'true' );
		[
			[ 'rect', { x:'6',  y:'6',  width:'36', height:'36', rx:'3',   stroke:'currentColor', 'stroke-width':'1.5' } ],
			[ 'rect', { x:'13', y:'13', width:'9',  height:'9',  rx:'1.5', fill:'currentColor' } ],
			[ 'rect', { x:'26', y:'13', width:'9',  height:'9',  rx:'1.5', fill:'currentColor' } ],
			[ 'rect', { x:'13', y:'26', width:'22', height:'8',  rx:'1.5', fill:'currentColor' } ],
		].forEach( ( [ tag, a ] ) => {
			const s = document.createElementNS( svgNS, tag );
			Object.entries( a ).forEach( ( [ k, v ] ) => s.setAttribute( k, v ) );
			svg.appendChild( s );
		} );
		return svg;
	};

	const makeZoomSVG = () => {
		const svg = document.createElementNS( svgNS, 'svg' );
		svg.setAttribute( 'width',   '18' );
		svg.setAttribute( 'height',  '18' );
		svg.setAttribute( 'viewBox', '0 0 24 24' );
		svg.setAttribute( 'fill',    'none' );
		svg.setAttribute( 'aria-hidden', 'true' );
		[
			[ 'circle', { cx:'11', cy:'11', r:'7', stroke:'currentColor', 'stroke-width':'2' } ],
			[ 'line', { x1:'16.5', y1:'16.5', x2:'22',  y2:'22',  stroke:'currentColor', 'stroke-width':'2', 'stroke-linecap':'round' } ],
			[ 'line', { x1:'8',   y1:'11',   x2:'14',  y2:'11',  stroke:'currentColor', 'stroke-width':'2', 'stroke-linecap':'round' } ],
			[ 'line', { x1:'11',  y1:'8',    x2:'11',  y2:'14',  stroke:'currentColor', 'stroke-width':'2', 'stroke-linecap':'round' } ],
		].forEach( ( [ tag, a ] ) => {
			const s = document.createElementNS( svgNS, tag );
			Object.entries( a ).forEach( ( [ k, v ] ) => s.setAttribute( k, v ) );
			svg.appendChild( s );
		} );
		return svg;
	};

	// ── Cleanup: flatten nested .fp-tabs-nav wrappers if any ──────────────
	// Si la page a déjà été chargée avec l'ancienne version buggée, on peut
	// se retrouver avec plusieurs .fp-tabs-nav imbriqués. Cette fonction
	// les aplatit pour ne garder que la structure attendue.

	const flattenNestedTabsNav = ( root ) => {
		// Trouve toutes les .fp-tabs-nav dans ce root
		const allNavs = root.querySelectorAll( '.fp-tabs-nav' );
		if ( allNavs.length <= 1 ) return; // Rien à faire

		// On garde la PREMIÈRE (la plus externe) et on aplatit le contenu
		const outer = allNavs[ 0 ];

		// Trouve la barre de tabs réelle (la plus profonde)
		const tabsBar = root.querySelector( '.fp-building-tabs' );
		if ( ! tabsBar ) return;

		// Vide complètement outer puis reconstruit avec : prev btn + tabsBar + next btn
		const prevBtn = outer.querySelector( '.fp-tabs-arrow--prev' );
		const nextBtn = outer.querySelector( '.fp-tabs-arrow--next' );

		// Détache la tabsBar et les boutons (pour les réutiliser)
		tabsBar.remove();
		prevBtn?.remove();
		nextBtn?.remove();

		// Vide tout le contenu corrompu
		outer.innerHTML = '';

		// Reconstruit proprement
		if ( prevBtn ) outer.appendChild( prevBtn );
		outer.appendChild( tabsBar );
		if ( nextBtn ) outer.appendChild( nextBtn );
	};

	// ── Modal ─────────────────────────────────────────────────────────────

	const initModal = ( root ) => {
		const modal    = root.querySelector( '#fp-modal' );
		const imgEl    = root.querySelector( '#fp-modal-img' );
		const caption  = root.querySelector( '#fp-modal-caption' );
		const closeBtn = root.querySelector( '#fp-modal-close' );
		const backdrop = root.querySelector( '.fp-modal__backdrop' );
		if ( ! modal || ! imgEl ) return null;

		let prevFocus = null;

		const open = ( src, alt ) => {
			prevFocus = document.activeElement;
			imgEl.setAttribute( 'src', src );
			imgEl.setAttribute( 'alt', alt );
			if ( caption ) caption.textContent = alt;
			modal.setAttribute( 'aria-hidden', 'false' );
			modal.classList.add( 'is-open' );
			document.body.style.overflow = 'hidden';
			closeBtn?.focus();
		};

		const close = () => {
			modal.setAttribute( 'aria-hidden', 'true' );
			modal.classList.remove( 'is-open' );
			document.body.style.overflow = '';
			imgEl.setAttribute( 'src', '' );
			prevFocus?.focus();
		};

		closeBtn?.addEventListener( 'click', close );
		backdrop?.addEventListener( 'click', close );
		modal.addEventListener( 'keydown', ( e ) => { if ( e.key === 'Escape' ) close(); } );

		return { open, close };
	};

	// ── Build detail content (XSS-safe) ──────────────────────────────────

	const buildDetailContent = ( category, room, modal ) => {
		const frag = document.createDocumentFragment();

		// Media
		const media = el( 'div', { class: 'fp-detail__media' } );

		if ( room.imageUrl ) {
			const btn = el( 'button', {
				class            : 'fp-detail__zoom-btn',
				type             : 'button',
				'data-img-src'   : room.imageUrl,
				'data-img-alt'   : room.title,
				'aria-label'     : `Enlarge image: ${ room.title }`,
			} );
			const img  = el( 'img', { src: room.imageUrl, alt: room.title, loading: 'lazy' } );
			const icon = el( 'span', { class: 'fp-detail__zoom-icon', 'aria-hidden': 'true' } );
			icon.appendChild( makeZoomSVG() );
			btn.appendChild( img );
			btn.appendChild( icon );
			media.appendChild( btn );
			if ( modal ) {
				btn.addEventListener( 'click', () => modal.open( room.imageUrl, room.title ) );
			}
		} else {
			const ph = el( 'span', { class: 'fp-detail__placeholder', 'aria-hidden': 'true' } );
			ph.appendChild( makePlaceholderSVG( 80 ) );
			media.appendChild( ph );
		}

		frag.appendChild( media );

		// Info
		const info = el( 'div', { class: 'fp-detail__info' } );
		info.appendChild( el( 'p',  { class: 'fp-detail__category' }, category.title ) );
		info.appendChild( el( 'h3', { class: 'fp-detail__title'    }, room.title ) );
		if ( room.subtitle ) {
			info.appendChild( el( 'p', { class: 'fp-detail__subtitle' }, room.subtitle ) );
		}
		if ( room.description ) {
			info.appendChild( el( 'p', { class: 'fp-detail__description' }, room.description ) );
		}
		frag.appendChild( info );

		return frag;
	};

	// ── Search match helper ───────────────────────────────────────────
	// 1 char  : \bX(\b|\d) → "M", "M1", "M2" oui — "Meeting", "Montbrillant" non
	// 2 chars : \bXY       → "H2", "H2K" oui — title+subtitle seulement
	// 3+ chars: \bXYZ      → tous les champs
	const buildMatchRegex = ( query ) => {
		const escaped = query.replace( /[.*+?^${}()|[\]\\]/g, '\\$&' );
		if ( query.length === 1 ) {
			return new RegExp( `\\b${ escaped }(\\b|\\d)`, 'i' );
		}
		return new RegExp( `\\b${ escaped }`, 'i' );
	};

	// Même logique avec groupe capturant pour le highlight
	const buildHighlightRegex = ( query ) => {
		const escaped = query.replace( /[.*+?^${}()|[\]\\]/g, '\\$&' );
		if ( query.length === 1 ) {
			return new RegExp( `(\\b${ escaped }(?=\\b|\\d))`, 'gi' );
		}
		return new RegExp( `(\\b${ escaped })`, 'gi' );
	};

	// 1-2 chars : title + subtitle seulement
	// 3+ chars  : tous les champs
	const matchesQuery = ( room, regex, query ) => {
		if ( query.length <= 2 ) {
			return regex.test( room.title    ?? '' )
			    || regex.test( room.subtitle ?? '' );
		}
		return regex.test( room.title       ?? '' )
		    || regex.test( room.subtitle    ?? '' )
		    || regex.test( room.description ?? '' );
	};

	const initBlock = ( root ) => {
		// ── Guard contre double-init ───────────────────────────────────────
		if ( root.dataset.fpInitialized === '1' ) return;
		root.dataset.fpInitialized = '1';

		// Nettoie les wrappers imbriqués si la page était déjà cassée
		flattenNestedTabsNav( root );

		const raw = root.getAttribute( 'data-categories' );
		if ( ! raw ) return;

		let categories;
		try {
			categories = JSON.parse( raw );
		} catch ( e ) {
			console.warn( 'ITU Floorplan: could not parse data-categories', e );
			return;
		}
		if ( ! Array.isArray( categories ) || categories.length === 0 ) return;

		// Parent .itu-floorplan (search bar lives here, not inside .fp-root)
		const floorplan = root.closest( '.itu-floorplan' ) ?? root;

		const modal = initModal( root );

		// ── Show room ─────────────────────────────────────────────────────
		const showRoom = ( catIndex, roomIndex ) => {
			const category = categories[ catIndex ];
			const room     = category?.rooms?.[ roomIndex ];
			if ( ! room ) return;

			// Update detail panel
			const detailEl = root.querySelector( `.fp-detail[data-detail="${ catIndex }"]` );
			if ( detailEl ) {
				detailEl.replaceChildren( buildDetailContent( category, room, modal ) );
				detailEl.classList.remove( 'is-visible' );
				void detailEl.offsetWidth; // force reflow for animation
				detailEl.classList.add( 'is-visible' );
			}

			// Update card states
			root.querySelectorAll( `.fp-panel[data-panel="${ catIndex }"] .fp-room-card` ).forEach( ( card ) => {
				const isSelected = parseInt( card.dataset.room, 10 ) === roomIndex;
				card.classList.toggle( 'is-active', isSelected );
				card.setAttribute( 'aria-pressed', String( isSelected ) );
			} );
		};

		// ── Activate building tab ─────────────────────────────────────────
		const showBuilding = ( catIndex ) => {
			root.querySelectorAll( '.fp-building-tab' ).forEach( ( tab ) => {
				const active = parseInt( tab.dataset.cat, 10 ) === catIndex;
				tab.classList.toggle( 'is-active', active );
				tab.setAttribute( 'aria-selected', String( active ) );
			} );

			root.querySelectorAll( '.fp-panel' ).forEach( ( panel ) => {
				panel.classList.toggle( 'is-active', parseInt( panel.dataset.panel, 10 ) === catIndex );
			} );

			if ( categories[ catIndex ]?.rooms?.length ) {
				showRoom( catIndex, 0 );
			}
		};

		// ── Event listeners ───────────────────────────────────────────────

		root.querySelectorAll( '.fp-building-tab' ).forEach( ( tab ) => {
			tab.addEventListener( 'click', () => {
				const searchInput = floorplan.querySelector( '.fp-search__input' );
				const query       = searchInput?.value.trim().toLowerCase() ?? '';

				if ( query ) {
					// En mode search : filtrer les résultats sur ce building
					const catIndex = parseInt( tab.dataset.cat, 10 );

					// Mettre à jour l'état actif des tabs
					root.querySelectorAll( '.fp-building-tab' ).forEach( t => {
						const active = parseInt( t.dataset.cat, 10 ) === catIndex;
						t.classList.toggle( 'is-active', active );
						t.setAttribute( 'aria-selected', String( active ) );
					} );

					// Régénérer les résultats pour ce building uniquement
					root.querySelectorAll( '.fp-search-results' ).forEach( el => el.remove() );
					const category      = categories[ catIndex ];
					const matchRegex2   = buildMatchRegex( query );
					const matchingRooms = ( category?.rooms ?? [] ).filter( room =>
						matchesQuery( room, matchRegex2, query )
					);
					const regex       = buildHighlightRegex( query );
					const resultsWrap = el( 'div', { class: 'fp-search-results' } );

					if ( matchingRooms.length > 0 ) {
						matchingRooms.forEach( ( room ) => {
							const card  = el( 'div', { class: 'fp-detail is-visible fp-search-result-card' } );
							const media = el( 'div', { class: 'fp-detail__media' } );
							if ( room.imageUrl ) {
								const btn = el( 'button', { class: 'fp-detail__zoom-btn', type: 'button', 'aria-label': `Enlarge: ${ room.title }` } );
								const img = el( 'img', { src: room.imageUrl, alt: room.title, loading: 'lazy' } );
								const ico = el( 'span', { class: 'fp-detail__zoom-icon', 'aria-hidden': 'true' } );
								ico.appendChild( makeZoomSVG() );
								btn.appendChild( img ); btn.appendChild( ico );
								if ( modal ) btn.addEventListener( 'click', () => modal.open( room.imageUrl, room.title ) );
								media.appendChild( btn );
							} else {
								const ph = el( 'span', { class: 'fp-detail__placeholder', 'aria-hidden': 'true' } );
								ph.appendChild( makePlaceholderSVG( 80 ) );
								media.appendChild( ph );
							}
							card.appendChild( media );
							const info    = el( 'div', { class: 'fp-detail__info' } );
							const titleEl = el( 'h3', { class: 'fp-detail__title' } );
							titleEl.innerHTML = room.title.replace( regex, '<mark class="highlight">$1</mark>' );
							info.appendChild( el( 'p', { class: 'fp-detail__category' }, category.title ) );
							info.appendChild( titleEl );
							if ( room.subtitle ) { const s = el( 'p', { class: 'fp-detail__subtitle' } ); s.innerHTML = room.subtitle.replace( regex, '<mark class="highlight">$1</mark>' ); info.appendChild( s ); }
							if ( room.description ) { const d = el( 'p', { class: 'fp-detail__description' } ); d.innerHTML = room.description.replace( regex, '<mark class="highlight">$1</mark>' ); info.appendChild( d ); }
							card.appendChild( info );
							resultsWrap.appendChild( card );
						} );
					} else {
						resultsWrap.appendChild( el( 'p', { class: 'fp-search-empty' }, 'No rooms match your search.' ) );
					}

					const tabsNavEl = root.querySelector( '.fp-tabs-nav' ) ?? root.querySelector( '.fp-building-tabs' );
					tabsNavEl ? tabsNavEl.after( resultsWrap ) : root.appendChild( resultsWrap );

				} else {
					// Pas de search : comportement normal
					clearBtn?.classList.remove( 'is-visible' );
					clearSearch( root, categories );
					showBuilding( parseInt( tab.dataset.cat, 10 ) );
				}
			} );
		} );

		root.querySelectorAll( '.fp-room-card' ).forEach( ( card ) => {
			card.addEventListener( 'click', () => {
				showRoom(
					parseInt( card.dataset.cat,  10 ),
					parseInt( card.dataset.room, 10 )
				);
			} );
		} );

		// Keyboard: arrows on building tabs
		root.querySelector( '.fp-building-tabs' )?.addEventListener( 'keydown', ( e ) => {
			if ( ! [ 'ArrowRight', 'ArrowLeft' ].includes( e.key ) ) return;
			e.preventDefault();
			const tabs = Array.from( root.querySelectorAll( '.fp-building-tab' ) );
			const idx  = tabs.indexOf( document.activeElement );
			if ( idx === -1 ) return;
			const next = e.key === 'ArrowRight'
				? Math.min( idx + 1, tabs.length - 1 )
				: Math.max( idx - 1, 0 );
			tabs[ next ]?.focus();
		} );

		// Keyboard: arrows on room cards (skip hidden active card)
		root.querySelectorAll( '.fp-room-grid' ).forEach( ( grid ) => {
			grid.addEventListener( 'keydown', ( e ) => {
				if ( ! [ 'ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp' ].includes( e.key ) ) return;
				e.preventDefault();
				const cards = Array.from( grid.querySelectorAll( '.fp-room-card:not(.is-active)' ) );
				const idx   = cards.indexOf( document.activeElement );
				if ( idx === -1 ) return;
				const cols = Math.max( 1, Math.round( grid.offsetWidth / ( cards[ 0 ]?.offsetWidth || 1 ) ) );
				let next = idx;
				if ( e.key === 'ArrowRight' ) next = Math.min( idx + 1, cards.length - 1 );
				if ( e.key === 'ArrowLeft'  ) next = Math.max( idx - 1, 0 );
				if ( e.key === 'ArrowDown'  ) next = Math.min( idx + cols, cards.length - 1 );
				if ( e.key === 'ArrowUp'    ) next = Math.max( idx - cols, 0 );
				cards[ next ]?.focus();
			} );
		} );

		// ── Search ────────────────────────────────────────────────────────

		const searchInput = floorplan.querySelector( '.fp-search__input' );
		const clearBtn    = floorplan.querySelector( '.fp-search__clear' );

		// ── Highlight helper : wraps query matches in <mark class="highlight">
		const highlightText = ( card, query ) => {
			[ '.fp-room-card__title', '.fp-room-card__subtitle2', '.fp-room-card__subtitle' ].forEach( ( sel ) => {
				const node = card.querySelector( sel );
				if ( ! node ) return;
				// Restore plain text first (strip previous marks)
				const plain = node.textContent;
				if ( ! query ) { node.textContent = plain; return; }
				const regex = buildHighlightRegex( query );
				node.innerHTML = plain.replace( regex, '<mark class="highlight">$1</mark>' );
			} );
		};

		const resetHighlights = ( root ) => {
			root.querySelectorAll( '.fp-room-card' ).forEach( ( card ) => {
				[ '.fp-room-card__title', '.fp-room-card__subtitle2', '.fp-room-card__subtitle' ].forEach( ( sel ) => {
					const node = card.querySelector( sel );
					if ( node ) node.textContent = node.textContent; // strips innerHTML marks
				} );
			} );
		};

		if ( searchInput ) {
			// Fermer le clavier mobile sur Enter
			searchInput.addEventListener( 'keydown', ( e ) => {
				if ( e.key === 'Enter' ) {
					searchInput.blur();
				}
			} );
			// Show/hide clear button
			searchInput.addEventListener( 'input', () => {
				const hasValue = searchInput.value.length > 0;
				clearBtn?.classList.toggle( 'is-visible', hasValue );
			} );

			// Clear button
			clearBtn?.addEventListener( 'click', () => {
				searchInput.value = '';
				clearBtn.classList.remove( 'is-visible' );
				resetHighlights( root );
				clearSearch( root, categories );
				showBuilding( 0 );
				searchInput.focus();
				// Désactiver les tags du résumé
				floorplan.querySelectorAll( '.fp-room-summary__tag' ).forEach( t => t.classList.remove( 'is-active' ) );
			} );

			searchInput.addEventListener( 'input', () => {
				const query = searchInput.value.trim();

				if ( ! query ) {
					resetHighlights( root );
					clearSearch( root, categories );
					showBuilding( 0 );
					return;
				}

				const matchRegex = buildMatchRegex( query );

				// Nettoyer les résultats précédents
				root.querySelectorAll( '.fp-search-results' ).forEach( el => el.remove() );

				// Tabs : activer ceux qui ont des résultats
				let totalMatches  = 0;
				let firstMatchCat = -1;
				const catMatches  = {}; // catIndex → matchingRooms[]

				categories.forEach( ( category, catIndex ) => {
					const matchingRooms = ( category.rooms ?? [] ).filter( room =>
						matchesQuery( room, matchRegex, query )
					);
					catMatches[ catIndex ] = matchingRooms;
					if ( matchingRooms.length > 0 ) {
						totalMatches += matchingRooms.length;
						if ( firstMatchCat === -1 ) firstMatchCat = catIndex;
					}
				} );

				// Mettre à jour les tabs (actif = a des résultats)
				root.querySelectorAll( '.fp-building-tab' ).forEach( ( tab ) => {
					const ci      = parseInt( tab.dataset.cat, 10 );
					const hasHits = ( catMatches[ ci ]?.length ?? 0 ) > 0;
					tab.classList.toggle( 'is-active', hasHits );
					tab.setAttribute( 'aria-selected', String( hasHits ) );
				} );

				// Cacher les panels natifs
				root.querySelectorAll( '.fp-panel' ).forEach( p => p.classList.remove( 'is-active' ) );

				// Conteneur des résultats
				const resultsWrap = el( 'div', { class: 'fp-search-results' } );
				const regex = buildHighlightRegex( query );

				categories.forEach( ( category, catIndex ) => {
					const matchingRooms = catMatches[ catIndex ];
					if ( ! matchingRooms?.length ) return;

					// H2 catégorie
					const heading = el( 'h2', { class: 'fp-search-results__heading' }, category.title );
					resultsWrap.appendChild( heading );

					matchingRooms.forEach( ( room ) => {
						const card  = el( 'div', { class: 'fp-detail is-visible fp-search-result-card' } );
						const media = el( 'div', { class: 'fp-detail__media' } );

						if ( room.imageUrl ) {
							const btn = el( 'button', { class: 'fp-detail__zoom-btn', type: 'button', 'aria-label': `Enlarge: ${ room.title }` } );
							const img = el( 'img', { src: room.imageUrl, alt: room.title, loading: 'lazy' } );
							const ico = el( 'span', { class: 'fp-detail__zoom-icon', 'aria-hidden': 'true' } );
							ico.appendChild( makeZoomSVG() );
							btn.appendChild( img );
							btn.appendChild( ico );
							if ( modal ) btn.addEventListener( 'click', () => modal.open( room.imageUrl, room.title ) );
							media.appendChild( btn );
						} else {
							const ph = el( 'span', { class: 'fp-detail__placeholder', 'aria-hidden': 'true' } );
							ph.appendChild( makePlaceholderSVG( 80 ) );
							media.appendChild( ph );
						}
						card.appendChild( media );

						const info    = el( 'div', { class: 'fp-detail__info' } );
						const titleEl = el( 'h3', { class: 'fp-detail__title' } );
						titleEl.innerHTML = room.title.replace( regex, '<mark class="highlight">$1</mark>' );
						info.appendChild( el( 'p', { class: 'fp-detail__category' }, category.title ) );
						info.appendChild( titleEl );

						if ( room.subtitle ) {
							const subEl = el( 'p', { class: 'fp-detail__subtitle' } );
							subEl.innerHTML = room.subtitle.replace( regex, '<mark class="highlight">$1</mark>' );
							info.appendChild( subEl );
						}
						if ( room.description ) {
							const descEl = el( 'p', { class: 'fp-detail__description' } );
							descEl.innerHTML = room.description.replace( regex, '<mark class="highlight">$1</mark>' );
							info.appendChild( descEl );
						}
						card.appendChild( info );
						resultsWrap.appendChild( card );
					} );
				} );

				if ( totalMatches === 0 ) {
					resultsWrap.appendChild( el( 'p', { class: 'fp-search-empty' }, 'No rooms match your search.' ) );
				}

				const tabsNavEl = root.querySelector( '.fp-tabs-nav' ) ?? root.querySelector( '.fp-building-tabs' );
				tabsNavEl ? tabsNavEl.after( resultsWrap ) : root.appendChild( resultsWrap );
			} );
		}

		// ── Room summary tags ─────────────────────────────────────────────
		const summaryTags = floorplan.querySelectorAll( '.fp-room-summary__tag' );
		if ( summaryTags.length && searchInput ) {
			summaryTags.forEach( ( tag ) => {
				tag.addEventListener( 'click', () => {
					const isActive = tag.classList.contains( 'is-active' );

					// Désactiver tous les tags
					summaryTags.forEach( t => t.classList.remove( 'is-active' ) );

					if ( isActive ) {
						// Déjà actif → clear la recherche
						searchInput.value = '';
						clearBtn?.classList.remove( 'is-visible' );
						resetHighlights( root );
						clearSearch( root, categories );
						showBuilding( 0 );
					} else {
						// Activer ce tag
						const value = tag.dataset.search ?? '';
						searchInput.value = value;
						clearBtn?.classList.toggle( 'is-visible', value.length > 0 );
						tag.classList.add( 'is-active' );
						searchInput.dispatchEvent( new Event( 'input', { bubbles: true } ) );
						searchInput.focus();
					}
				} );
			} );

			// Désactiver le tag actif quand on efface la recherche
			searchInput.addEventListener( 'input', () => {
				if ( ! searchInput.value ) {
					summaryTags.forEach( t => t.classList.remove( 'is-active' ) );
				}
			} );
		}

		// ── Tabs nav wrapper (arrows + scroll) ────────────────────────────
		// FIX : ne créer le wrapper que s'il n'existe PAS déjà.
		// Sinon réutiliser celui du HTML statique et juste attacher les listeners.

		const tabsBar = root.querySelector( '.fp-building-tabs' );
		if ( tabsBar ) {
			let nav, prevBtn, nextBtn;

			// Le wrapper existe-t-il déjà (HTML statique de save.js) ?
			if ( tabsBar.parentNode.classList.contains( 'fp-tabs-nav' ) ) {
				// Réutiliser l'existant
				nav     = tabsBar.parentNode;
				prevBtn = nav.querySelector( '.fp-tabs-arrow--prev' );
				nextBtn = nav.querySelector( '.fp-tabs-arrow--next' );
			} else {
				// Sinon créer un nouveau wrapper
				nav = el( 'div', { class: 'fp-tabs-nav' } );
				tabsBar.parentNode.insertBefore( nav, tabsBar );

				prevBtn = el( 'button', {
					class       : 'fp-tabs-arrow fp-tabs-arrow--prev',
					type        : 'button',
					'aria-label': 'Previous',
				}, '‹' );

				nextBtn = el( 'button', {
					class       : 'fp-tabs-arrow fp-tabs-arrow--next',
					type        : 'button',
					'aria-label': 'Next',
				}, '›' );

				nav.appendChild( prevBtn );
				nav.appendChild( tabsBar );
				nav.appendChild( nextBtn );
			}

			const scrollAmount = 120;

			const updateState = () => {
				const hasOverflow = tabsBar.scrollWidth > tabsBar.clientWidth + 4;
				const atStart     = tabsBar.scrollLeft <= 4;
				const atEnd       = tabsBar.scrollLeft >= tabsBar.scrollWidth - tabsBar.clientWidth - 4;

				nav.classList.toggle( 'tabs-has-overflow', hasOverflow );
				nav.classList.toggle( 'tabs-at-start',     ! hasOverflow || atStart );
				nav.classList.toggle( 'tabs-at-end',       ! hasOverflow || atEnd );
			};

			prevBtn?.addEventListener( 'click', () => {
				tabsBar.scrollLeft -= scrollAmount;
				setTimeout( updateState, 300 );
			} );
			nextBtn?.addEventListener( 'click', () => {
				tabsBar.scrollLeft += scrollAmount;
				setTimeout( updateState, 300 );
			} );

			tabsBar.addEventListener( 'scroll', updateState, { passive: true } );
			window.addEventListener( 'resize',  updateState );

			// Expose un refresh pour le modal (recalcule overflow quand modal s'ouvre)
			root.__refreshFloorplanTabs = updateState;

			// Init
			updateState();
		}

		root.querySelectorAll( '.fp-detail' ).forEach( detailEl => {
			detailEl.classList.add( 'is-visible' );
		} );
		showBuilding( 0 );
	};

	// ── Search reset helper ───────────────────────────────────────────────

	const clearSearch = ( root, categories ) => {
		// Supprimer le conteneur de résultats injecté par la recherche
		root.querySelectorAll( '.fp-search-results' ).forEach( el => el.remove() );

		// Remettre is-active sur la première card de chaque panel
		root.querySelectorAll( '.fp-panel' ).forEach( panel => {
			const firstCard = panel.querySelector( '.fp-room-card' );
			if ( firstCard ) firstCard.classList.add( 'is-active' );
		} );

		// Restore tab/panel active states (only first building visible)
		root.querySelectorAll( '.fp-building-tab' ).forEach( ( tab, i ) => {
			tab.classList.toggle( 'is-active', i === 0 );
			tab.setAttribute( 'aria-selected', i === 0 ? 'true' : 'false' );
		} );
		root.querySelectorAll( '.fp-panel' ).forEach( ( panel, i ) => {
			panel.classList.toggle( 'is-active', i === 0 );
		} );
	};

	// ── Expose un refresh global pour les modals ──────────────────────────
	window.brBlocksRefreshFloorplan = function ( scope ) {
		const root = scope || document;
		root.querySelectorAll( '.fp-root' ).forEach( ( fpRoot ) => {
			if ( typeof fpRoot.__refreshFloorplanTabs === 'function' ) {
				fpRoot.__refreshFloorplanTabs();
			}
		} );
	};

	// ── Boot ──────────────────────────────────────────────────────────────
	document.addEventListener( 'DOMContentLoaded', () => {
		document.querySelectorAll( '.fp-root' ).forEach( initBlock );
	} );

} )();